import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { getDashboardStats, getMissions, getOpportunities, getVehicles, getMyBids } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Package, 
  Truck, 
  TrendingUp, 
  Clock, 
  ArrowRight,
  Plus,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { t } = useTranslation();
  const { user, isEnterprise, isCarrier } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState({ unread_count: 0 });

  useEffect(() => {
    fetchDashboardData();
    fetchNotifications();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const statsData = await getDashboardStats();
      setStats(statsData);

      if (isEnterprise) {
        const missions = await getMissions();
        setItems(missions.slice(0, 5));
      } else if (isCarrier) {
        const opportunities = await getOpportunities();
        setItems(opportunities.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('fleetflow_token');
      if (!token) return;
      
      const res = await fetch(`${API_URL}/api/notifications?unread_only=true&limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: t('status.pending'), className: 'status-pending' },
      matched: { label: t('status.matched'), className: 'status-matched' },
      in_progress: { label: t('status.inProgress'), className: 'status-in_progress' },
      completed: { label: t('status.completed'), className: 'status-completed' },
      cancelled: { label: t('status.cancelled'), className: 'status-cancelled' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100' };
    return <Badge className={`badge ${config.className}`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
              {t('dashboard.welcome')}, {user?.company_name}
            </h1>
            <p className="text-slate-500 mt-1">
              {t('dashboard.overview')}
            </p>
          </div>
          <div className="flex gap-3">
            {notifications.unread_count > 0 && (
              <Link to="/notifications">
                <Button variant="outline" className="relative">
                  <Bell className="w-4 h-4 mr-2" />
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                    {notifications.unread_count}
                  </span>
                </Button>
              </Link>
            )}
            {isEnterprise && (
              <Link to="/missions/new">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="new-mission-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('dashboard.newMission')}
                </Button>
              </Link>
            )}
            {isCarrier && (
              <Link to="/fleet/new">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="new-vehicle-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('dashboard.newVehicle')}
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="stat-value text-xl">{stats?.total_missions || 0}</div>
                  <div className="stat-label">{t('dashboard.stats.missions')}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <div className="stat-value text-xl">{stats?.active_missions || 0}</div>
                  <div className="stat-label">{t('dashboard.stats.active')}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="stat-value text-xl">{stats?.completed_missions || 0}</div>
                  <div className="stat-label">{t('dashboard.stats.completed')}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="stat-value text-xl font-['JetBrains_Mono']">
                    {stats?.total_revenue?.toLocaleString('fr-FR') || 0}€
                  </div>
                  <div className="stat-label">{t('dashboard.stats.revenue')}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-['Chivo']">
                  {isEnterprise ? t('dashboard.recentMissions') : t('dashboard.opportunities')}
                </CardTitle>
                <Link to={isEnterprise ? '/missions' : '/opportunities'}>
                  <Button variant="ghost" size="sm" className="text-orange-500">
                    {t('dashboard.viewAll')} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {items.length > 0 ? (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <Link 
                        key={item.id} 
                        to={`/missions/${item.id}`}
                        className="block"
                        data-testid={`mission-item-${item.id}`}
                      >
                        <div className="mission-card">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-slate-900 truncate">{item.title}</h3>
                                {getStatusBadge(item.status)}
                              </div>
                              <p className="text-sm text-slate-500 truncate">
                                {item.pickup_address} → {item.delivery_address}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className="text-slate-500">
                                  <span className="font-['JetBrains_Mono'] text-slate-900">{item.weight_kg}</span> kg
                                </span>
                                <span className="text-slate-500">
                                  <span className="font-['JetBrains_Mono'] text-slate-900">{item.bids_count}</span> {t('mission.bids').toLowerCase()}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-['JetBrains_Mono'] font-semibold text-slate-900">
                                {item.budget_min}€ - {item.budget_max}€
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                {new Date(item.deadline).toLocaleDateString('fr-FR')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state py-8">
                    <AlertCircle className="empty-state-icon" />
                    <p>{isEnterprise ? t('mission.noBids') : t('dashboard.opportunities')}</p>
                    {isEnterprise && (
                      <Link to="/missions/new">
                        <Button className="mt-4" variant="outline">
                          <Plus className="w-4 h-4 mr-2" />
                          {t('mission.create')}
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Chivo']">{t('dashboard.activity')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">{t('dashboard.totalBids')}</span>
                  <span className="font-['JetBrains_Mono'] font-semibold">{stats?.total_bids || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">{t('dashboard.pendingBids')}</span>
                  <span className="font-['JetBrains_Mono'] font-semibold text-orange-500">{stats?.pending_bids || 0}</span>
                </div>
              </CardContent>
            </Card>

            {isEnterprise && (
              <Card className="bg-slate-900 text-white">
                <CardContent className="p-6">
                  <h3 className="font-['Chivo'] font-semibold text-lg mb-2">SmartMatch</h3>
                  <p className="text-slate-300 text-sm mb-4">
                    {t('landing.features.smartmatch.desc')}
                  </p>
                  <Link to="/smartmatch">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600">
                      {t('common.search', 'Lancer une recherche')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {isCarrier && (
              <Card className="bg-slate-900 text-white">
                <CardContent className="p-6">
                  <h3 className="font-['Chivo'] font-semibold text-lg mb-2">{t('nav.fleet')}</h3>
                  <p className="text-slate-300 text-sm mb-4">
                    {t('landing.features.tracking.desc')}
                  </p>
                  <Link to="/fleet">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600">
                      {t('dashboard.viewAll')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Recent Notifications */}
            {notifications.notifications?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-['Chivo'] flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {notifications.notifications.slice(0, 3).map((notif) => (
                      <div key={notif.id} className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm font-medium">{notif.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{notif.body}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
